import {
    ArtsXsmall,
    AutoLight,
    Blocks,
    BusinessXsmall,
    CharityAndCauses,
    DiscoSmall,
    FilmMediaSmall,
    Flag,
    FoodDrinkXsmall,
    Globe,
    Handshake,
    HobbiesSmall,
    HolidayCalendarSmall,
    HomeAndLifestyle,
    MicXsmall,
    SchoolAndActivities,
    ScienceAndTech,
    Spirituality,
    SportsSmall,
    Stethoscope,
    Thread,
    TravelLight,
} from '@eventbrite/eds-iconography';
import { GenericLazyString, gettext } from '@eventbrite/i18n';
import React from 'react';

export interface ICategoryToIconMap {
    icon: JSX.Element;
    category: GenericLazyString;
    searchParam: string;
    heapSelector: string;
    tagSelector: string;
}

//Eventbrite Category ID Mapping
// Found in SEARCH SERVICE REPO --> search_service/common/constants/tags.py#L119
export enum EVENTBRITE_CATEGORY_MAP {
    AUTO_BOAT_AIR = 118,
    BUSINESS = 101,
    CHARITY = 111,
    COMMUNITY = 113,
    FAMILY_EDUCATION = 115,
    FASHION = 106,
    FILM = 104,
    FOOD = 110,
    HEALTH = 107,
    HOBBIES = 119,
    HOME_LIFESTYLE = 117,
    MUSIC = 103,
    NIGHTLIFE = 121,
    OTHER = 199,
    PERFORMING = 105,
    POLITICS = 112,
    RELIGION = 114,
    SCHOOL = 120,
    SCIENCE = 102,
    SEASONAL = 116,
    SPORTS = 108,
    TRAVEL = 109,
}

export const categoryToIconMap: Array<ICategoryToIconMap> = [
    {
        category: gettext('All'),
        searchParam: 'all',
        icon: <Globe height="24px" width="24px" />,
        heapSelector: 'CategoryFilterBar-All',
        tagSelector: '',
    },
    {
        category: gettext('Music'),
        searchParam: 'music',
        icon: <MicXsmall />,
        heapSelector: 'CategoryFilterBar-Music',
        tagSelector: `EventbriteCategory/${EVENTBRITE_CATEGORY_MAP.MUSIC}`,
    },
    {
        category: gettext('Arts'),
        searchParam: 'arts',
        icon: <ArtsXsmall />,
        heapSelector: 'CategoryFilterBar-Arts',
        tagSelector: `EventbriteCategory/${EVENTBRITE_CATEGORY_MAP.PERFORMING}`,
    },
    {
        category: gettext('Auto, Boat, and Air'),
        searchParam: 'auto, boat, and air',
        icon: <AutoLight height="24px" width="24px" />,
        heapSelector: 'CategoryFilterBar-Auto, Boat, and Air',
        tagSelector: `EventbriteCategory/${EVENTBRITE_CATEGORY_MAP.AUTO_BOAT_AIR}`,
    },
    {
        category: gettext('Business'),
        searchParam: 'business',
        icon: <BusinessXsmall />,
        heapSelector: 'CategoryFilterBar-Business',
        tagSelector: `EventbriteCategory/${EVENTBRITE_CATEGORY_MAP.BUSINESS}`,
    },
    {
        category: gettext('Charity & Causes'),
        searchParam: 'charity & causes',
        icon: <CharityAndCauses />,
        heapSelector: 'CategoryFilterBar-Charity & Causes',
        tagSelector: `EventbriteCategory/${EVENTBRITE_CATEGORY_MAP.CHARITY}`,
    },
    {
        category: gettext('Community'),
        searchParam: 'community',
        icon: <Handshake />,
        heapSelector: 'CategoryFilterBar-Community',
        tagSelector: `EventbriteCategory/${EVENTBRITE_CATEGORY_MAP.COMMUNITY}`,
    },
    {
        category: gettext('Family & Education'),
        searchParam: 'family & education',
        icon: <Blocks />,
        heapSelector: 'CategoryFilterBar-Family & Education',
        tagSelector: `EventbriteCategory/${EVENTBRITE_CATEGORY_MAP.FAMILY_EDUCATION}`,
    },
    {
        category: gettext('Fashion'),
        searchParam: 'fashion',
        icon: <Thread />,
        heapSelector: 'CategoryFilterBar-Fashion',
        tagSelector: `EventbriteCategory/${EVENTBRITE_CATEGORY_MAP.FASHION}`,
    },
    {
        category: gettext('Film & Media'),
        searchParam: 'film & media',
        icon: <FilmMediaSmall />,
        heapSelector: 'CategoryFilterBar-Film & Media',
        tagSelector: `EventbriteCategory/${EVENTBRITE_CATEGORY_MAP.FILM}`,
    },
    {
        category: gettext('Food & Drink'),
        searchParam: 'food & drink',
        icon: <FoodDrinkXsmall />,
        heapSelector: 'CategoryFilterBar-Food & Drink',
        tagSelector: `EventbriteCategory/${EVENTBRITE_CATEGORY_MAP.FOOD}`,
    },
    {
        category: gettext('Government'),
        searchParam: 'government',
        icon: <Flag />,
        heapSelector: 'CategoryFilterBar-Government',
        tagSelector: `EventbriteCategory/${EVENTBRITE_CATEGORY_MAP.POLITICS}`,
    },
    {
        category: gettext('Health'),
        searchParam: 'health',
        icon: <Stethoscope />,
        heapSelector: 'CategoryFilterBar-Health',
        tagSelector: `EventbriteCategory/${EVENTBRITE_CATEGORY_MAP.HEALTH}`,
    },
    {
        category: gettext('Hobbies'),
        searchParam: 'hobbies',
        icon: <HobbiesSmall />,
        heapSelector: 'CategoryFilterBar-Hobbies',
        tagSelector: `EventbriteCategory/${EVENTBRITE_CATEGORY_MAP.HOBBIES}`,
    },
    {
        category: gettext('Holiday'),
        searchParam: 'holiday',
        icon: <HolidayCalendarSmall />,
        heapSelector: 'CategoryFilterBar-Holiday',
        tagSelector: `EventbriteCategory/${EVENTBRITE_CATEGORY_MAP.SEASONAL}`,
    },
    {
        category: gettext('Home & Lifestyle'),
        searchParam: 'home & lifestyle',
        icon: <HomeAndLifestyle />,
        heapSelector: 'CategoryFilterBar-Home & Lifestyle',
        tagSelector: `EventbriteCategory/${EVENTBRITE_CATEGORY_MAP.HOME_LIFESTYLE}`,
    },
    {
        category: gettext('School Activities'),
        searchParam: 'school activities',
        icon: <SchoolAndActivities />,
        heapSelector: 'CategoryFilterBar-School Activities',
        tagSelector: `EventbriteCategory/${EVENTBRITE_CATEGORY_MAP.SCHOOL}`,
    },
    {
        category: gettext('Science & Tech'),
        searchParam: 'science & tech',
        icon: <ScienceAndTech />,
        heapSelector: 'CategoryFilterBar-Science & Tech',
        tagSelector: `EventbriteCategory/${EVENTBRITE_CATEGORY_MAP.SCIENCE}`,
    },
    {
        category: gettext('Spirituality'),
        searchParam: 'spirituality',
        icon: <Spirituality />,
        heapSelector: 'CategoryFilterBar-Spirituality',
        tagSelector: `EventbriteCategory/${EVENTBRITE_CATEGORY_MAP.RELIGION}`,
    },
    {
        category: gettext('Sport & Fitness'),
        searchParam: 'sport & fitness',
        icon: <SportsSmall />,
        heapSelector: 'CategoryFilterBar-Sport & Fitness',
        tagSelector: `EventbriteCategory/${EVENTBRITE_CATEGORY_MAP.SPORTS}`,
    },
    {
        category: gettext('Travel & Outdoor'),
        searchParam: 'travel & outdoor',
        icon: <TravelLight height="18px" width="18px" />,
        heapSelector: 'CategoryFilterBar-Travel & Outdoor',
        tagSelector: `EventbriteCategory/${EVENTBRITE_CATEGORY_MAP.TRAVEL}`,
    },
    {
        category: gettext('Clubs & Parties'),
        searchParam: 'clubs & parties',
        icon: <DiscoSmall />,
        heapSelector: 'CategoryFilterBar-Clubs & Parties',
        tagSelector: `EventbriteCategory/${EVENTBRITE_CATEGORY_MAP.NIGHTLIFE}`,
    },
];
